<template>
  <div class="position-management" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <!-- Main Content -->
    <div class="dashboard-content">
      <div class="page-container">
        <!-- Modern page header -->
        <div class="modern-page-header">
          <div class="header-content">
            <div class="title-section">
              <h1 class="dashboard-title">Designation Management</h1>
              <p class="dashboard-subtitle">
                <span class="icon">💼</span>
                Manage designations, hierarchies, and assignments
              </p>
            </div>
            <div class="header-actions">
              <div class="view-selector">
                <button class="view-btn" :class="{ 'active': viewMode === 'grid' }" @click="viewMode = 'grid'">
                  <i class="bx bx-grid-alt"></i> Grid
                </button>
                <button class="view-btn" :class="{ 'active': viewMode === 'list' }" @click="viewMode = 'list'">
                  <i class="bx bx-list-ul"></i> List
                </button>
              </div>
              <button class="refresh-btn" @click="refreshData" :disabled="loading">
                <i class="bx bx-refresh" :class="{ 'spin': loading }"></i>
                {{ loading ? 'Loading...' : 'Refresh' }}
              </button>
            </div>
          </div>
          
          <div class="breadcrumb-section">
            <div class="breadcrumb">
              <span class="breadcrumb-item" @click="goToHome">
                <span class="icon">🏠</span>
                <span class="text">Home</span>
              </span>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-item" @click="goToEmployeeManagement">
                <span class="icon">👥</span>
                <span class="text">Employee Management</span>
              </span>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Designations</span>
            </div>
          </div>
        </div>

        <!-- Stats Cards -->
        <div class="metrics-grid">
          <!-- Total Positions -->
          <div class="metric-card gradient-1">
            <div class="metric-icon">
              <i class="bx bx-briefcase"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">Total Designations</p>
              <h2 class="metric-value">{{ formatNumber(statistics?.totalPositions || 0) }}</h2>
              <div class="metric-footer">
                <span class="trend-indicator neutral">
                  <i class="bx bx-category"></i>
                  <span>All designations</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Active Positions -->
          <div class="metric-card gradient-2">
            <div class="metric-icon">
              <i class="bx bx-check-circle"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">Active Designations</p>
              <h2 class="metric-value">{{ formatNumber(statistics?.activePositions || 0) }}</h2>
              <div class="metric-footer">
                <span class="trend-badge active">{{ ((statistics?.activePositions / statistics?.totalPositions) * 100 || 0).toFixed(1) }}% active</span>
              </div>
            </div>
          </div>

          <!-- Total Employees -->
          <div class="metric-card gradient-3">
            <div class="metric-icon">
              <i class="bx bx-group"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">Total Employees</p>
              <h2 class="metric-value">{{ formatNumber(statistics?.totalEmployees || 0) }}</h2>
              <div class="metric-footer">
                <span class="trend-indicator positive">
                  <i class="bx bx-user-check"></i>
                  <span>Active workforce</span>
                </span>
              </div>
            </div>
          </div>

          <!-- Vacant Positions -->
          <div class="metric-card gradient-4">
            <div class="metric-icon">
              <i class="bx bx-trending-up"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">Vacant Designations</p>
              <h2 class="metric-value">{{ formatNumber(statistics?.vacantPositions || 0) }}</h2>
              <div class="metric-footer">
                <span class="trend-indicator neutral">
                  <i class="bx bx-user-plus"></i>
                  <span>Available for hiring</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Stats -->
        <div class="quick-stats">
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-icon">
                <i class="bx bx-trending-up"></i>
              </div>
              <div class="stat-content">
                <h4>{{ getHighLevelPositions }}</h4>
                <p>High Level (8-10)</p>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">
                <i class="bx bx-trending-up"></i>
              </div>
              <div class="stat-content">
                <h4>{{ getMidLevelPositions }}</h4>
                <p>Mid Level (4-7)</p>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">
                <i class="bx bx-trending-up"></i>
              </div>
              <div class="stat-content">
                <h4>{{ getEntryLevelPositions }}</h4>
                <p>Entry Level (1-3)</p>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">
                <i class="bx bx-building"></i>
              </div>
              <div class="stat-content">
                <h4>{{ departments.length }}</h4>
                <p>Departments</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Job Groups Section (view added job groups like positions) -->
        <div class="job-groups-section hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-tag"></i> Job Groups</h5>
            <button @click="openJobGroupsModal" class="action-btn primary">
              <i class="bx bx-plus"></i> Add Job Group
            </button>
          </div>
          <div class="hr-chart-body">
            <div v-if="jobGroupsLoading" class="loading-state">
              <div class="spinner"></div>
              <p>Loading job groups...</p>
            </div>
            <div v-else-if="jobGroups.length === 0" class="empty-state">
              <div class="empty-icon"><i class="bx bx-tag"></i></div>
              <h3>No job groups yet</h3>
              <p>Add job groups to use when creating positions and employees.</p>
              <button @click="openJobGroupsModal" class="btn-outline"><i class="bx bx-plus"></i> Add Job Group</button>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th style="text-align:center">Status</th>
                    <th style="width:100px;text-align:center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(jg, idx) in jobGroups" :key="jg.id">
                    <td>{{ idx + 1 }}</td>
                    <td><strong>{{ jg.code }}</strong></td>
                    <td>{{ jg.name }}</td>
                    <td>{{ jg.description || '–' }}</td>
                  <td style="text-align:center">
                    <span
                      :class="[
                        'status-pill',
                        (jg.is_active === 1 || jg.is_active === '1' || jg.is_active === true) ? 'active' : 'inactive'
                      ]"
                    >
                      {{
                        (jg.is_active === 1 || jg.is_active === '1' || jg.is_active === true)
                          ? 'Active'
                          : 'Inactive'
                      }}
                    </span>
                  </td>
                    <td style="text-align:center">
                      <button type="button" class="icon-btn" title="Edit" @click="editJobGroupThenOpenModal(jg)">
                        <i class="bx bx-edit-alt"></i>
                      </button>
                      <button type="button" class="icon-btn danger" title="Delete" @click="deleteJobGroup(jg)">
                        <i class="bx bx-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Filters and Actions -->
        <div class="filters-section">
          <div class="filters-header">
            <h3><i class="bx bx-filter-alt"></i> Filter & Search</h3>
            <div class="selected-count" v-if="selectedPositions.length > 0">
              {{ selectedPositions.length }} selected
              <button class="clear-selection" @click="clearSelection">
                <i class="bx bx-x"></i>
              </button>
            </div>
          </div>
          
          <div class="filters-grid">
            <!-- Search Box -->
            <div class="search-box">
              <i class="bx bx-search"></i>
              <input 
                v-model="searchQuery" 
                @input="debounceSearch"
                type="text" 
                placeholder="Search by position name, code, or department..."
                :class="{'is-loading': loading}"
              >
              <button v-if="searchQuery" @click="clearSearch" class="clear-btn">
                <i class="bx bx-x"></i>
              </button>
            </div>

            <!-- Department Filter -->
            <div class="filter-group">
              <label><i class="bx bx-building"></i> Department</label>
              <select v-model="departmentFilter" @change="onFilterChange" class="form-select">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.departmentName }}
                </option>
              </select>
            </div>

            <!-- Status Filter -->
            <div class="filter-group">
              <label><i class="bx bx-user-circle"></i> Status</label>
              <select v-model="statusFilter" @change="onFilterChange" class="form-select">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <!-- Level Filter -->
            <div class="filter-group">
              <label><i class="bx bx-trending-up"></i> Level</label>
              <select v-model="levelFilter" @change="onFilterChange" class="form-select">
                <option value="">All Levels</option>
                <option value="1-3">Entry Level (1-3)</option>
                <option value="4-7">Mid Level (4-7)</option>
                <option value="8-10">High Level (8-10)</option>
              </select>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button @click="exportPositions" class="action-btn export" :disabled="loading || filteredPositions.length === 0">
              <i :class="isDownloading ? 'bx bx-loader-alt bx-spin' : 'bx bx-export'"></i>
              {{ isDownloading ? 'Exporting...' : 'Export' }}
            </button>
            
            <button @click="openJobGroupsModal" class="action-btn secondary">
              <i class="bx bx-tag"></i>
              Job Groups
            </button>

            <button @click="openAddPositionModal" class="action-btn primary">
              <i class="bx bx-plus"></i>
              Add Designation
            </button>
          </div>
        </div>

        <!-- Positions Section -->
        <div class="positions-section">
          <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>Loading position data...</p>
          </div>

          <div v-else-if="filteredPositions.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="bx bx-briefcase"></i>
            </div>
            <h3>No designations found</h3>
            <p>Try adjusting your search or filters</p>
            <button @click="resetFilters" class="btn-outline">Reset Filters</button>
          </div>

          <!-- Grid View -->
          <div v-else-if="viewMode === 'grid'" class="positions-grid">
            <div v-for="position in filteredPositions" :key="position.id" class="position-card">
              <div class="card-header">
                <div class="position-avatar" :class="getStatusClass(position)">
                  {{ getPositionInitials(position) }}
                </div>
                <div class="position-actions">
                  <button @click="viewPosition(position)" class="icon-btn" title="View">
                    <i class="bx bx-show"></i>
                  </button>
                  <button @click="editPosition(position)" class="icon-btn" title="Edit">
                    <i class="bx bx-edit"></i>
                  </button>
                  <div class="dropdown" :class="{ 'is-open': isDropdownOpen(position.id, 'grid') }">
                    <button
                      type="button"
                      class="icon-btn dropdown-toggle"
                      aria-haspopup="true"
                      :aria-expanded="isDropdownOpen(position.id, 'grid')"
                      @click.stop="toggleDropdown(position.id, 'grid')"
                    >
                      <i class="bx bx-dots-vertical-rounded"></i>
                    </button>
                    <div class="dropdown-menu" :class="{ show: isDropdownOpen(position.id, 'grid') }" @click.stop>
                      <a @click.prevent="assignToEmployee(position)" href="javascript:void(0)" class="dropdown-item">
                        <i class="bx bx-user-plus"></i> Assign Employee
                      </a>
                      <a @click.prevent="viewPositionEmployees(position)" href="javascript:void(0)" class="dropdown-item">
                        <i class="bx bx-group"></i> View Employees
                      </a>
                      <div class="dropdown-divider"></div>
                      <a v-if="position.id !== 98" @click.prevent="deletePosition(position)" href="javascript:void(0)" class="dropdown-item text-danger">
                        <i class="bx bx-trash"></i> Delete
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="card-body">
                <h4>{{ position.positionName || position.name }}</h4>
                <p class="position-code">{{ position.code || 'N/A' }}</p>
                
                <div class="position-info">
                  <div class="info-item">
                    <i class="bx bx-building"></i>
                    <span>{{ position.department_name || position.department || 'No Department' }}</span>
                  </div>
                  <div class="info-item">
                    <i class="bx bx-trending-up"></i>
                    <span>Level {{ position.level || 1 }}</span>
                  </div>
                  <div class="info-item">
                    <i class="bx bx-group"></i>
                    <span>Inpost: {{ position.currentEmployees || position.current_employees || 0 }} / Proposed: {{ position.maxEmployees || position.max_employees || '∞' }}</span>
                  </div>
                  <div class="info-item">
                    <i class="bx bx-arrow-back"></i>
                    <span>Supervisor: {{ position.reports_to_name || position.reportsTo || 'None' }}</span>
                  </div>
                </div>
                
                <div class="position-status">
                  <span :class="getStatusBadgeClass(position)">
                    {{ getStatusText(position) }}
                  </span>
                  <small class="text-muted">
                    Min: KES{{ formatSalary(position.salary_min || position.minSalary) }}
                  </small>
                </div>

                <!-- Capacity progress bar -->
                <div v-if="position.maxEmployees > 0 || position.max_employees > 0" class="capacity-progress">
                  <div class="capacity-header">
                    <small>Fill Rate</small>
                    <small>{{ getFillPercentage(position) }}%</small>
                  </div>
                  <div class="progress-bar">
                    <div class="progress-fill"
                         :class="getProgressBarClass(position)"
                         :style="{ width: getFillPercentage(position) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Table View -->
          <div v-else class="positions-table">
            <div class="table-responsive">
              <table class="table">
                <thead>
                  <tr>
                    <th width="50">
                      <input type="checkbox" v-model="selectAll" @change="toggleSelectAll">
                    </th>
                    <th>Designation</th>
                    <th>Code</th>
                    <th>Department</th>
                    <th>Level</th>
                    <th>Inpost / Proposed</th>
                    <th>Status</th>
                    <th class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="position in filteredPositions" :key="position.id" 
                      :class="{ 'selected': selectedPositions.includes(position.id) }">
                    <td>
                      <input type="checkbox" :value="position.id" v-model="selectedPositions">
                    </td>
                    <td>
                      <div class="position-cell">
                        <div class="avatar">
                          {{ getPositionInitials(position) }}
                        </div>
                        <div>
                          <h6>{{ position.positionName || position.name }}</h6>
                          <small class="text-muted">{{ position.description ? truncate(position.description, 30) : 'No description' }}</small>
                        </div>
                      </div>
                    </td>
                    <td>{{ position.code || 'N/A' }}</td>
                    <td>
                      <span class="dept-badge">{{ position.department_name || position.department || 'N/A' }}</span>
                    </td>
                    <td>
                      <span class="level-badge">{{ position.level || 1 }}</span>
                    </td>
                    <td>
                      <span :class="getEmployeeCountClass(position)">
                        {{ position.currentEmployees || position.current_employees || 0 }} / {{ position.maxEmployees || position.max_employees || '∞' }}
                      </span>
                    </td>
                    <td>
                      <span :class="getStatusBadgeClass(position)">
                        {{ getStatusText(position) }}
                      </span>
                    </td>
                    <td class="text-center">
                      <div class="btn-group">
                        <button @click="viewPosition(position)" class="btn-sm btn-icon" title="View">
                          <i class="bx bx-show"></i>
                        </button>
                        <button @click="editPosition(position)" class="btn-sm btn-icon" title="Edit">
                          <i class="bx bx-edit"></i>
                        </button>
                        <div class="dropdown" :class="{ 'is-open': isDropdownOpen(position.id, 'list') }">
                          <button
                            type="button"
                            class="btn-sm btn-icon dropdown-toggle"
                            aria-haspopup="true"
                            :aria-expanded="isDropdownOpen(position.id, 'list')"
                            @click.stop="toggleDropdown(position.id, 'list')"
                          >
                            <i class="bx bx-dots-vertical-rounded"></i>
                          </button>
                          <div class="dropdown-menu" :class="{ show: isDropdownOpen(position.id, 'list') }" @click.stop>
                            <a @click.prevent="assignToEmployee(position)" href="javascript:void(0)" class="dropdown-item">
                              <i class="bx bx-user-plus"></i> Assign
                            </a>
                            <a @click.prevent="viewPositionEmployees(position)" href="javascript:void(0)" class="dropdown-item">
                              <i class="bx bx-group"></i> View Employees
                            </a>
                            <div class="dropdown-divider"></div>
                            <a v-if="position.id !== 98" @click.prevent="deletePosition(position)" href="javascript:void(0)" class="dropdown-item text-danger">
                              <i class="bx bx-trash"></i> Delete
                            </a>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-section" v-if="filteredPositions.length > 0">
            <div class="pagination-info">
              Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, filteredPositions.length) }} of {{ filteredPositions.length }} entries
            </div>
            <div class="pagination-controls">
              <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
                <i class="bx bx-chevron-left"></i>
              </button>
              
              <template v-for="page in visiblePages" :key="page">
                <button v-if="page === '...'" class="pagination-btn disabled">...</button>
                <button v-else @click="goToPage(page)" 
                        :class="['pagination-btn', { 'active': page === currentPage }]">
                  {{ page }}
                </button>
              </template>
              
              <button @click="nextPage" :disabled="currentPage === totalPages || totalPages === 0" class="pagination-btn">
                <i class="bx bx-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Bulk Actions Bar -->
        <div v-if="selectedPositions.length > 0" class="bulk-actions-bar">
          <div class="bulk-actions-content">
            <span class="selected-count">
              <i class="bx bx-check-circle"></i>
              {{ selectedPositions.length }} designation(s) selected
            </span>
            <div class="bulk-actions">
              <button class="bulk-btn" @click="bulkExport">
                <i class="bx bx-export"></i> Export Selected
              </button>
              <button v-if="!selectedHasPosition98" class="bulk-btn danger" @click="bulkDelete">
                <i class="bx bx-trash"></i> Delete Selected
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <footer class="modern-footer">
          <div class="footer-content">
            <div class="footer-left">
              <p class="system-info">
                <i class="bx bx-data"></i>
                {{ filteredPositions.length }} Designations • {{ statistics?.totalEmployees || 0 }} Total Employees
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Modals -->
    <AddEditPositionModal
        v-if="showAddEditModal"
        :position="selectedPosition"
        :mode="modalMode"
        :departments="departments"
        :positions="positions"
        @close="closeAddEditModal"
        @saved="handlePositionSaved"
    />

    <AssignPositionModal
        v-if="showAssignModal"
        :position="selectedPosition"
        :employees="availableEmployees"
        @close="closeAssignModal"
        @assigned="handlePositionAssigned"
    />

    <ViewPositionModal
        v-if="showViewModal"
        :position="selectedPosition"
        :employees="positionEmployees"
        @close="closeViewModal"
        @edit="editPositionFromView"
        @assign="assignFromView"
    />

    <!-- Job Groups Modal (same look & feel as Add Position) -->
    <div v-if="showJobGroupsModal" class="modal-overlay" @click.self="closeJobGroupsModal">
      <div class="modal-container modal-md">
        <div class="modal-header">
          <h3><i class="bx bx-tag"></i> Job Groups</h3>
          <button type="button" class="modal-close" @click="closeJobGroupsModal">
            <i class="bx bx-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="jobgroup-form">
            <div class="form-row">
              <div class="form-group">
                <label>Code <span class="required">*</span></label>
                <input v-model="newJobGroup.code" type="text" class="form-control" placeholder="e.g., JG K">
              </div>
              <div class="form-group">
                <label>Name <span class="required">*</span></label>
                <input v-model="newJobGroup.name" type="text" class="form-control" placeholder="e.g., Job Group K">
              </div>
            </div>
            <div class="form-group">
              <label>Description</label>
              <textarea v-model="newJobGroup.description" rows="2" class="form-control" placeholder="Describe this job group (optional)"></textarea>
            </div>
            <div class="form-group inline">
              <label>Status</label>
              <select v-model="newJobGroup.status" class="form-control">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div class="form-actions">
              <button class="btn btn-secondary" @click="resetJobGroupForm" type="button">Clear</button>
              <button class="btn btn-primary" :disabled="savingJobGroup" @click="saveJobGroup" type="button">
                <i v-if="savingJobGroup" class="bx bx-loader-alt bx-spin"></i>
                {{ savingJobGroup ? 'Saving...' : 'Save Job Group' }}
              </button>
            </div>
          </div>

          <div class="jobgroup-list">
            <h4>Existing Job Groups</h4>
            <div v-if="jobGroupsLoading" class="loading-state">
              <div class="spinner"></div>
              <p>Loading job groups...</p>
            </div>
            <div v-else-if="jobGroups.length === 0" class="empty-state">
              <p>No job groups defined yet. Add the first one above.</p>
            </div>
            <table v-else class="jobgroup-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th style="width: 80px; text-align:right;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="jg in jobGroups" :key="jg.id">
                  <td>{{ jg.code }}</td>
                  <td>{{ jg.name }}</td>
                  <td>{{ jg.description || '-' }}</td>
                  <td>
                    <span
                      :class="[
                        'status-pill',
                        (jg.is_active === 1 || jg.is_active === '1' || jg.is_active === true) ? 'active' : 'inactive'
                      ]"
                    >
                      {{
                        (jg.is_active === 1 || jg.is_active === '1' || jg.is_active === true)
                          ? 'Active'
                          : 'Inactive'
                      }}
                    </span>
                  </td>
                  <td style="text-align:right;">
                    <button class="icon-btn" title="Edit" @click="editJobGroup(jg)">
                      <i class="bx bx-edit-alt"></i>
                    </button>
                    <button class="icon-btn danger" title="Delete" @click="deleteJobGroup(jg)">
                      <i class="bx bx-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/Navigation.vue";
import AddEditPositionModal from "@/components/employeeRegister/modals/AddEditPositionModal.vue";
import AssignPositionModal from "@/components/employeeRegister/modals/AssignPositionModal.vue";
import ViewPositionModal from "@/components/employeeRegister/modals/ViewPositionModal.vue";
import { authUrl, execute, getRight } from "@/api";
import moment from "moment";

export default {
  name: "PositionManagement",
  components: {
    Navigation,
    AddEditPositionModal,
    AssignPositionModal,
    ViewPositionModal
  },
  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      isDownloading: false,
      viewMode: 'grid', // 'grid' or 'list'
      
      // Filters
      searchQuery: '',
      departmentFilter: '',
      statusFilter: '',
      levelFilter: '',
      
      // Pagination
      currentPage: 1,
      pageSize: 12,
      
      // Data
      positions: [],
      departments: [],
      employees: [], // Add employees array for statistics
      
      // Stats
      statistics: {
        totalPositions: 0,
        activePositions: 0,
        totalEmployees: 0,
        vacantPositions: 0
      },
      
      // Selection
      selectedPositions: [],
      selectAll: false,
      
      // Modal controls
      showAddEditModal: false,
      showAssignModal: false,
      showViewModal: false,
      
      selectedPosition: null,
      modalMode: 'add',
      
      availableEmployees: [],
      positionEmployees: [],
      
      openDropdownId: null,
      
      // Utilities
      searchTimeout: null,
      currentYear: new Date().getFullYear(),

      // Job groups
      showJobGroupsModal: false,
      jobGroupsLoading: false,
      savingJobGroup: false,
      jobGroups: [],
      editingJobGroupId: null,
      newJobGroup: {
        code: '',
        name: '',
        description: '',
        status: 'active'
      }
    };
  },
  computed: {
    filteredPositions() {
      if (!this.positions || this.positions.length === 0) {
        return [];
      }
      
      let filtered = [...this.positions];
      
      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(pos => {
          const positionName = (pos.positionName || pos.name || '').toLowerCase();
          const code = (pos.code || '').toLowerCase();
          const description = (pos.description || '').toLowerCase();
          const department = (pos.department_name || pos.department || '').toLowerCase();
          
          return positionName.includes(query) || 
                 code.includes(query) || 
                 description.includes(query) || 
                 department.includes(query);
        });
      }
      
      // Department filter
      if (this.departmentFilter) {
        filtered = filtered.filter(pos => 
          pos.department_id == this.departmentFilter
        );
      }
      
      // Status filter
      if (this.statusFilter) {
        filtered = filtered.filter(pos => {
          const status = pos.is_active || pos.status;
          return status == 1 || status === 'active';
        });
      }
      
      // Level filter
      if (this.levelFilter) {
        const [min, max] = this.levelFilter.split('-').map(Number);
        filtered = filtered.filter(pos => {
          const level = pos.level || 1;
          return level >= min && level <= max;
        });
      }
      
      return filtered;
    },
    
    getHighLevelPositions() {
      return this.positions.filter(p => (p.level || 1) >= 8).length;
    },
    
    getMidLevelPositions() {
      return this.positions.filter(p => {
        const level = p.level || 1;
        return level >= 4 && level <= 7;
      }).length;
    },
    
    getEntryLevelPositions() {
      return this.positions.filter(p => (p.level || 1) <= 3).length;
    },
    
    selectedHasPosition98() {
      return this.selectedPositions.some(id => id == 98);
    },
    
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    
    endIndex() {
      return Math.min(this.currentPage * this.pageSize, this.filteredPositions.length);
    },
    
    totalPages() {
      return Math.ceil(this.filteredPositions.length / this.pageSize);
    },
    
    paginatedPositions() {
      return this.filteredPositions.slice(this.startIndex, this.endIndex);
    },
    
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const maxVisible = 5;

      if (total <= maxVisible) {
        for (let i = 1; i <= total; i++) pages.push(i);
        return pages;
      }

      let start = Math.max(current - Math.floor(maxVisible / 2), 1);
      let end = start + maxVisible - 1;

      if (end > total) {
        end = total;
        start = end - maxVisible + 1;
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) pages.push(i);

      if (end < total) {
        if (end < total - 1) pages.push('...');
        pages.push(total);
      }

      return pages;
    }
  },
  mounted() {
    // Check permission for POSITION MANAGEMENT
    if (!getRight('POSITION MANAGEMENT')) {
      this.$router.replace('/dashboard');
      return;
    }
    sessionStorage.setItem("selected", "position-management");
    sessionStorage.setItem("selectedSub", "position-management");
    this.initializeData();
    this._closeDropdownHandler = () => this.closeDropdowns();
    document.addEventListener('click', this._closeDropdownHandler);
  },
  beforeDestroy() {
    if (this._closeDropdownHandler) {
      document.removeEventListener('click', this._closeDropdownHandler);
    }
  },
  methods: {
    // Navigation
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    
    goToHome() {
      this.$router.push('/dashboard');
    },
    
    goToEmployeeManagement() {
      this.$router.push('/employee-register');
    },
    
    // Data initialization
    initializeData() {
      // Load all data in parallel for better performance (including job groups)
      Promise.all([
        this.loadPositions(),
        this.loadDepartments(),
        this.loadEmployees(),
        this.loadJobGroups()
      ]).then(() => {
        this.calculateStatistics(); // Calculate stats after all data is loaded
      }).catch(error => {
        console.error("Error initializing data:", error);
      });
    },
    
    refreshData() {
      this.initializeData();
    },

    // Job groups modal actions
    openJobGroupsModal() {
      this.showJobGroupsModal = true;
      this.loadJobGroups();
    },

    closeJobGroupsModal() {
      this.showJobGroupsModal = false;
    },

    resetJobGroupForm() {
      this.newJobGroup = {
        code: '',
        name: '',
        description: '',
        status: 'active'
      };
      this.editingJobGroupId = null;
    },

    async loadJobGroups() {
      this.jobGroupsLoading = true;
      const data = new FormData();
      data.append("function", "getJobGroups");
      try {
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          let raw = res.data.data?.jobGroups ?? res.data.jobGroups ?? [];
          if (raw && raw.Records) raw = raw.Records;
          if (!Array.isArray(raw) && raw) raw = [raw];
          this.jobGroups = raw || [];
        } else {
          this.jobGroups = [];
        }
      } catch (e) {
        console.error("Error loading job groups:", e);
        this.jobGroups = [];
      } finally {
        this.jobGroupsLoading = false;
      }
    },

    editJobGroupThenOpenModal(jg) {
      this.editJobGroup(jg);
      this.showJobGroupsModal = true;
    },

    async saveJobGroup() {
      if (!this.newJobGroup.code || !this.newJobGroup.name) {
        alert("Code and Name are required for Job Group");
        return;
      }

      this.savingJobGroup = true;
      const data = new FormData();
      data.append("function", this.editingJobGroupId ? "updateJobGroup" : "createJobGroup");
      if (this.editingJobGroupId) {
        data.append("id", this.editingJobGroupId);
      }
      data.append("code", this.newJobGroup.code.trim());
      data.append("name", this.newJobGroup.name.trim());
      data.append("description", this.newJobGroup.description?.trim() || '');
      data.append("status", this.newJobGroup.status || 'active');

      try {
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          alert("Job group saved successfully");
          this.resetJobGroupForm();
          this.loadJobGroups();
        } else {
          alert(res?.data?.message || "Failed to save job group");
        }
      } catch (e) {
        console.error("Error saving job group:", e);
        alert("Error saving job group");
      } finally {
        this.savingJobGroup = false;
      }
    },

    editJobGroup(jg) {
      this.newJobGroup = {
        code: jg.code || '',
        name: jg.name || '',
        description: jg.description || '',
        status: (jg.is_active === 1 || jg.is_active === '1' || jg.is_active === true) ? 'active' : 'inactive'
      };
      this.editingJobGroupId = jg.id;
    },

    async deleteJobGroup(jg) {
      if (!jg || !jg.id) return;
      if (!confirm(`Are you sure you want to delete job group "${jg.code} - ${jg.name}"?`)) {
        return;
      }

      const data = new FormData();
      data.append("function", "deleteJobGroup");
      data.append("id", jg.id);

      try {
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          alert("Job group deleted successfully");
          this.loadJobGroups();
        } else {
          alert(res?.data?.message || "Failed to delete job group");
        }
      } catch (e) {
        console.error("Error deleting job group:", e);
        alert("Error deleting job group");
      }
    },
    
    // Load employees for statistics
    loadEmployees() {
      return new Promise((resolve, reject) => {
        const data = new FormData();
        data.append("function", "getEmployees");

        execute(data, authUrl)
          .then((res) => {
            if (res?.data?.success) {
              this.employees = res.data.data?.employees || [];
            } else {
              this.employees = [];
            }
            resolve();
          })
          .catch((e) => {
            console.error("Error loading employees:", e);
            this.employees = [];
            reject(e);
          });
      });
    },
    
    // Calculate statistics from loaded data
    calculateStatistics() {
      // Total positions
      const totalPositions = this.positions.length;
      
      // Active positions (is_active = 1)
      const activePositions = this.positions.filter(p => 
        p.is_active == 1 || p.is_active === '1' || p.is_active === true || p.status === 'active'
      ).length;
      
      // Total active employees
      const totalEmployees = this.employees.filter(e => 
        e.status === 'Active' || e.status === 'active'
      ).length;
      
      // Vacant positions (positions with no employees assigned)
      // Create a map of position IDs that have employees
      const positionsWithEmployees = new Set();
      this.employees.forEach(emp => {
        if (emp.positionId) {
          positionsWithEmployees.add(parseInt(emp.positionId));
        }
      });
      
      // Count positions that don't have any employees
      const vacantPositions = this.positions.filter(p => {
        const positionId = parseInt(p.id);
        return !positionsWithEmployees.has(positionId);
      }).length;
      
      this.statistics = {
        totalPositions,
        activePositions,
        totalEmployees,
        vacantPositions
      };
      
      console.log("Statistics calculated:", this.statistics);
    },
    
    // Data fetching
    loadPositions() {
      return new Promise((resolve, reject) => {
        this.loading = true;

        const data = new FormData();
        data.append("function", "getPositions");

        execute(data, authUrl)
          .then((res) => {
            this.loading = false;
            const d = res && res.data;
            if (d == null || d === '') {
              this.positions = [];
              alert('Server returned no data. Check that the API URL is correct and the positions table exists.');
              resolve();
              return;
            }
            const list = (d.data && d.data.positions) || d.positions || (Array.isArray(d.data) ? d.data : null) || [];
            if (d.success === true || Array.isArray(list)) {
              this.positions = Array.isArray(list) ? list : [];
              if (this.employees.length > 0) this.calculateStatistics();
            } else {
              this.positions = [];
              const err = (typeof d === 'string' ? d : d.message) || 'Failed to load positions';
              alert(err);
            }
            resolve();
          })
          .catch((e) => {
            this.loading = false;
            this.positions = [];
            const err = e?.response?.data?.message || (typeof e?.response?.data === 'string' ? e.response.data : null) || e?.message || 'Error loading positions';
            alert(err);
            reject(e);
          });
      });
    },

    loadDepartments() {
      return new Promise((resolve) => {
        const data = new FormData();
        data.append("function", "getDepartments");

        execute(data, authUrl)
          .then((res) => {
            if (res?.data?.success) {
              this.departments = res.data.data?.departments || [];
            } else {
              this.departments = [];
              if (res?.data?.message) {
                console.warn("Failed to load departments:", res.data.message);
              }
            }
            resolve();
          })
          .catch((e) => {
            console.error("Error loading departments:", e);
            this.departments = [];
            resolve();
          });
      });
    },

    loadAvailableEmployees() {
      const data = new FormData();
      data.append("function", "getAvailableEmployees");
      if (this.selectedPosition) {
        data.append("positionId", this.selectedPosition.id);
      }

      execute(data, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            this.availableEmployees = res.data.data?.employees || [];
          } else {
            this.availableEmployees = [];
            const errorMsg = res?.data?.message || "Failed to load available employees";
            this.$toast?.error?.(errorMsg) || console.error(errorMsg);
          }
        })
        .catch((e) => {
          console.error("Error loading available employees:", e);
          this.availableEmployees = [];
          const errorMsg = e?.message || "Unknown error";
          this.$toast?.error?.(`Error loading available employees: ${errorMsg}`) || console.error(errorMsg);
        });
    },

    loadPositionEmployees(positionId) {
      const data = new FormData();
      data.append("function", "getPositionEmployees");
      data.append("positionId", positionId);

      execute(data, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            this.positionEmployees = res.data.data?.employees || [];
          } else {
            this.positionEmployees = [];
          }
        })
        .catch((e) => {
          console.error("Error loading position employees:", e);
          this.positionEmployees = [];
        });
    },

    // Keep this method for backward compatibility but it won't be used
    loadStatistics() {
      // This method is kept for compatibility but now just calls calculateStatistics
      this.calculateStatistics();
    },
    
    // Search and filters
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
      }, 500);
    },
    
    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
    },
    
    onFilterChange() {
      this.currentPage = 1;
    },
    
    resetFilters() {
      this.searchQuery = '';
      this.departmentFilter = '';
      this.statusFilter = '';
      this.levelFilter = '';
      this.currentPage = 1;
    },
    
    // Position actions
    openAddPositionModal() {
      this.modalMode = 'add';
      this.selectedPosition = null;
      this.showAddEditModal = true;
    },

    normalizePosition(position) {
      if (!position) return null;
      const name = position.positionName || position.name || '';
      return {
        ...position,
        positionName: name,
        name,
        status: position.is_active === 1 || position.is_active === true || position.status === 'active' ? 'active' : 'inactive',
        minSalary: position.salary_min ?? position.minSalary ?? 0,
        maxSalary: position.salary_max ?? position.maxSalary ?? 0,
        maxEmployees: position.max_employees ?? position.maxEmployees ?? 0,
        currentEmployees: position.current_employees ?? position.currentEmployees ?? 0,
        department: position.department_name ?? position.department ?? 'Not assigned'
      };
    },

    dropdownKey(id, scope) {
      if (id == null || id === '') return null;
      const prefix = scope ? String(scope) + '-' : '';
      return prefix + String(id);
    },

    isDropdownOpen(id, scope) {
      return this.openDropdownId === this.dropdownKey(id, scope);
    },

    toggleDropdown(id, scope) {
      const key = this.dropdownKey(id, scope);
      this.openDropdownId = this.openDropdownId === key ? null : key;
    },

    closeDropdowns() {
      this.openDropdownId = null;
    },

    editPosition(position) {
      this.closeDropdowns();
      this.modalMode = 'edit';
      this.selectedPosition = this.normalizePosition(position);
      this.showAddEditModal = true;
    },

    editPositionFromView(position) {
      this.closeViewModal();
      setTimeout(() => {
        this.editPosition(position);
      }, 300);
    },

    viewPosition(position) {
      this.closeDropdowns();
      const p = this.normalizePosition(position);
      this.selectedPosition = p;
      this.positionEmployees = [];
      this.showViewModal = true;
      this.loadPositionEmployees(p.id);
    },

    viewPositionEmployees(position) {
      this.viewPosition(position);
    },

    assignToEmployee(position) {
      this.closeDropdowns();
      const p = this.normalizePosition(position);
      this.selectedPosition = p;
      this.availableEmployees = [];
      this.showAssignModal = true;
    },

    assignFromView(position) {
      this.closeViewModal();
      setTimeout(() => {
        this.assignToEmployee(position);
      }, 300);
    },

    deletePosition(position) {
      // Prevent deletion of position 98 (Admin/Superuser)
      if (position.id == 98) {
        this.$toast.error("Position 98 (Admin/Superuser) cannot be deleted");
        return;
      }

      if (!confirm(`Are you sure you want to delete the position "${position.positionName || position.name}"?`)) {
        return;
      }

      const data = new FormData();
      data.append("function", "deletePosition");
      data.append("positionId", position.id);

      execute(data, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            this.$toast.success("Position deleted successfully");
            this.loadPositions();
          } else {
            this.$toast.error(res?.data?.message || "Failed to delete position");
          }
        })
        .catch((e) => {
          this.$toast.error("Error deleting position: " + (e?.message || "Unknown error"));
        });
    },
    
    // Bulk actions
    bulkExport() {
      this.isDownloading = true;
      setTimeout(() => {
        this.isDownloading = false;
        this.$toast.success("Export completed successfully");
      }, 1000);
    },
    
    bulkDelete() {
      if (this.selectedHasPosition98) {
        this.$toast.error("Cannot delete Position 98 (Admin/Superuser)");
        return;
      }
      
      if (!confirm(`Are you sure you want to delete ${this.selectedPositions.length} selected positions?`)) {
        return;
      }
      
      // Implement bulk delete logic
      this.$toast.success("Bulk delete feature coming soon");
    },
    
    // Modal handlers
    closeAddEditModal() {
      this.showAddEditModal = false;
      this.selectedPosition = null;
    },

    closeAssignModal() {
      this.showAssignModal = false;
      this.selectedPosition = null;
      this.availableEmployees = [];
    },

    closeViewModal() {
      this.showViewModal = false;
      this.selectedPosition = null;
      this.positionEmployees = [];
    },

    handlePositionSaved() {
      this.closeAddEditModal();
      this.loadPositions().then(() => {
        this.loadEmployees().then(() => {
          this.calculateStatistics();
        });
      });
    },

    handlePositionAssigned() {
      const pid = this.selectedPosition?.id;
      this.closeAssignModal();
      this.loadPositions().then(() => {
        this.loadEmployees().then(() => {
          this.calculateStatistics();
        });
      });
    },
    
    // Selection
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedPositions = this.paginatedPositions.map(pos => pos.id);
      } else {
        this.selectedPositions = [];
      }
    },
    
    clearSelection() {
      this.selectedPositions = [];
      this.selectAll = false;
    },
    
    // Pagination
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    
    goToPage(page) {
      if (typeof page === 'number' && page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
      }
    },
    
    // Export
    exportPositions() {
      if (this.isDownloading || this.filteredPositions.length === 0) return;
      this.isDownloading = true;
      setTimeout(() => {
        this.isDownloading = false;
        this.$toast.success("Positions exported successfully");
      }, 1000);
    },
    
    // UI helpers
    formatNumber(num) {
      return num?.toLocaleString() || '0';
    },
    
    formatSalary(salary) {
      if (!salary || salary === 0) return '0';
      return Number(salary).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    
    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    
    getPositionInitials(position) {
      const name = position.positionName || position.name || '';
      return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().substring(0, 2);
    },
    
    getStatusClass(position) {
      const status = position.is_active || position.status;
      if (status === 1 || status === '1' || status === 'active' || status === true) {
        return 'active';
      }
      return 'inactive';
    },
    
    getStatusBadgeClass(position) {
      const status = position.is_active || position.status;
      if (status === 1 || status === '1' || status === 'active' || status === true) {
        return 'status-badge active';
      }
      return 'status-badge inactive';
    },
    
    getStatusText(position) {
      const status = position.is_active || position.status;
      if (status === 1 || status === '1' || status === 'active' || status === true) {
        return 'Active';
      }
      return 'Inactive';
    },
    
    getEmployeeCountClass(position) {
      const current = position.currentEmployees || position.current_employees || 0;
      const max = position.maxEmployees || position.max_employees || 0;
      
      if (max > 0 && current >= max) return 'badge danger';
      if (max > 0 && current >= max * 0.8) return 'badge warning';
      return 'badge success';
    },
    
    getFillPercentage(position) {
      const max = position.maxEmployees || position.max_employees || 0;
      const current = position.currentEmployees || position.current_employees || 0;
      
      if (!max || max === 0) {
        return 0;
      }
      const percentage = (current / max) * 100;
      return Math.min(Math.round(percentage), 100);
    },

    getProgressBarClass(position) {
      const percentage = this.getFillPercentage(position);
      if (percentage >= 90) return 'progress-danger';
      if (percentage >= 75) return 'progress-warning';
      if (percentage >= 50) return 'progress-info';
      return 'progress-success';
    },
    
    // UI Actions
    showHelp() {
      // Implement help modal
      console.log('Show help');
    },
    
    showSettings() {
      this.$router.push('/settings');
    }
  },
  watch: {
    // Watch for changes in positions or employees and recalculate statistics
    positions: {
      handler() {
        if (this.employees.length > 0) {
          this.calculateStatistics();
        }
      },
      deep: true
    },
    employees: {
      handler() {
        if (this.positions.length > 0) {
          this.calculateStatistics();
        }
      },
      deep: true
    }
  }
};
</script>

<style src="@/assets/css/hr-page-shared.css"></style>
<style scoped>
/* Job Groups section on page */
.job-groups-section {
  margin-bottom: 24px;
}

.job-groups-section .loading-state,
.job-groups-section .empty-state {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

/* Main Layout */
.position-management {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.position-management.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 20px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

/* Modern Page Header - Copy from EmployeeRegister */
.modern-page-header {
  background: #ffffff;
  border-radius: 20px;
  padding: 25px 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  overflow: visible;
}

.modern-page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #059669 0%, #16a34a 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.title-section {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}

.dashboard-title {
  font-size: clamp(1.25rem, 4vw, 32px);
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  word-wrap: break-word;
  background: linear-gradient(90deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 16px;
  margin: 10px 0 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-subtitle .icon {
  font-size: 18px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.view-selector {
  display: flex;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.view-btn {
  padding: 10px 20px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-weight: 600;
  transition: all 0.2s;
}

.view-btn:hover {
  background: #e2e8f0;
  color: #059669;
}

.view-btn.active {
  background: #059669;
  color: white;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(79, 70, 229, 0.3);
}

.refresh-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.refresh-btn i.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.breadcrumb-section {
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
}

.breadcrumb-item:hover {
  color: #059669;
}

.breadcrumb-current {
  color: #94a3b8;
  font-weight: 600;
}

.breadcrumb-separator {
  color: #cbd5e1;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.metric-card {
  position: relative;
  border-radius: 20px;
  padding: 25px;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  background: #ffffff;
  min-height: 160px;
  cursor: pointer;
}

.metric-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.gradient-1 {
  background: linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%);
  border-left: 4px solid #3b82f6;
}

.gradient-2 {
  background: linear-gradient(135deg, #d1fae5 0%, #f0fdf4 100%);
  border-left: 4px solid #10b981;
}

.gradient-3 {
  background: linear-gradient(135deg, #fef3c7 0%, #fefce8 100%);
  border-left: 4px solid #f59e0b;
}

.gradient-4 {
  background: linear-gradient(135deg, #f3e8ff 0%, #faf5ff 100%);
  border-left: 4px solid #8b5cf6;
}

.metric-icon {
  position: absolute;
  top: 25px;
  right: 25px;
  width: 60px;
  height: 60px;
  background: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.metric-icon i {
  font-size: 28px;
}

.gradient-1 .metric-icon i { color: #3b82f6; }
.gradient-2 .metric-icon i { color: #10b981; }
.gradient-3 .metric-icon i { color: #f59e0b; }
.gradient-4 .metric-icon i { color: #8b5cf6; }

.metric-content {
  padding-right: 70px;
}

.metric-label {
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value {
  color: #1e293b;
  font-size: 36px;
  font-weight: 800;
  margin: 0;
  line-height: 1;
}

.metric-footer {
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.trend-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}

.trend-indicator.positive {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.trend-indicator.neutral {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

.trend-badge {
  display: inline-flex;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}

.trend-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

/* Quick Stats */
.quick-stats {
  background: white;
  border-radius: 20px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s;
}

.stat-item:hover {
  background: #e2e8f0;
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.stat-content h4 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
}

.stat-content p {
  margin: 5px 0 0 0;
  color: #64748b;
  font-size: 14px;
}

/* Filters Section */
.filters-section {
  background: white;
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.filters-header h3 {
  margin: 0;
  font-size: 18px;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
}

.clear-selection {
  background: none;
  border: none;
  color: #0369a1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.search-box {
  position: relative;
  grid-column: 1 / -1;
}

.search-box i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

.search-box input {
  width: 100%;
  padding: 12px 45px 12px 45px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.search-box input.is-loading {
  background-color: #f8f9fa;
}

.clear-btn {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 0;
}

.filter-group label {
  display: block;
  margin-bottom: 8px;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-select {
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  font-size: 14px;
  cursor: pointer;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.action-btn.export {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.action-btn.primary {
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  color: white;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Positions Section */
.positions-section {
  background: white;
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.loading-state .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(79, 70, 229, 0.1);
  border-top-color: #059669;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #94a3b8;
  font-size: 32px;
}

.empty-state h3 {
  margin: 0 0 10px 0;
  color: #1e293b;
}

.empty-state p {
  color: #64748b;
  margin: 0 0 20px 0;
}

.btn-outline {
  padding: 10px 24px;
  background: white;
  border: 1px solid #059669;
  color: #059669;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-outline:hover {
  background: #059669;
  color: white;
}

/* Grid View */
.positions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
}

.position-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: visible;
  transition: all 0.3s ease;
  position: relative;
  z-index: 0;
}

.position-card:hover {
  z-index: 10;
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.card-header {
  background: #f8fafc;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.position-avatar {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
}

.position-avatar.active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.position-avatar.inactive {
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
}

.position-actions {
  display: flex;
  gap: 5px;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.icon-btn:hover {
  background: #f1f5f9;
  color: #059669;
}

.dropdown {
  position: relative;
}

.dropdown.is-open {
  z-index: 2000;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 180px;
  z-index: 1050;
  display: none;
}

.dropdown-menu.show {
  display: block !important;
}

.dropdown-item {
  padding: 10px 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
}

.dropdown-item:hover {
  background: #f1f5f9;
  color: #059669;
}

.dropdown-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 5px 0;
}

.card-body {
  padding: 20px;
}

.card-body h4 {
  margin: 0 0 5px 0;
  color: #1e293b;
}

.position-code {
  color: #64748b;
  font-size: 14px;
  margin: 0 0 15px 0;
}

.position-info {
  margin-bottom: 15px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #475569;
}

.info-item i {
  color: #64748b;
  width: 20px;
}

.position-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.status-badge.inactive {
  background: rgba(100, 116, 139, 0.1);
  color: #475569;
}

.capacity-progress {
  margin-top: 15px;
}

.capacity-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 12px;
  color: #64748b;
}

.progress-bar {
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.progress-success { background: #10b981; }
.progress-info { background: #3b82f6; }
.progress-warning { background: #f59e0b; }
.progress-danger { background: #ef4444; }

/* Table View */
.positions-table {
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table td.text-center {
  position: relative;
  overflow: visible;
}

.table tbody tr {
  position: relative;
}

.table th {
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #475569;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}

.table td {
  padding: 15px;
  border-bottom: 1px solid #e2e8f0;
}

.table tbody tr:hover {
  background: #f8fafc;
}

.table tbody tr.selected {
  background: #e0f2fe;
}

.position-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #059669 0%, #16a34a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.dept-badge {
  padding: 4px 12px;
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.level-badge {
  padding: 4px 8px;
  background: #f1f5f9;
  color: #475569;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.badge {
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.badge.success {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.badge.warning {
  background: rgba(245, 158, 11, 0.1);
  color: #92400e;
}

.badge.danger {
  background: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

.btn-group {
  display: flex;
  gap: 5px;
}

.btn-sm {
  padding: 6px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;
}

.btn-icon:hover {
  background: #f1f5f9;
  color: #059669;
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  color: #64748b;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 5px;
}

.pagination-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #475569;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f1f5f9;
  color: #059669;
}

.pagination-btn.active {
  background: #059669;
  color: white;
  border-color: #059669;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.disabled {
  border: none;
  background: none;
  cursor: default;
}

/* Bulk Actions Bar */
.bulk-actions-bar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border-radius: 16px;
  padding: 15px 25px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  min-width: 500px;
}

.bulk-actions-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-count {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #059669;
}

.bulk-actions {
  display: flex;
  gap: 8px;
}

.bulk-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.bulk-btn:first-child {
  background: #f1f5f9;
  color: #475569;
}

.bulk-btn.danger {
  background: #fee2e2;
  color: #991b1b;
}

.bulk-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Footer */
.modern-footer {
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  padding: 25px 0;
  margin-top: 40px;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.04);
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.footer-left p,
.footer-right p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

.system-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 13px;
}

.system-info i {
  color: #10b981;
}

.powered-by {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-style: italic;
}

.powered-by i {
  color: #059669;
}

.footer-links {
  display: flex;
  gap: 20px;
  margin-top: 8px;
}

.footer-links a {
  color: #64748b;
  text-decoration: none;
  font-size: 13px;
  transition: color 0.2s;
}

.footer-links a:hover {
  color: #059669;
}

/* ========= Job Groups Modal (reuse AddEditPositionModal look) ========= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

.modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}

.modal-container.modal-md {
  max-width: 700px;
}

.modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-header h3 i {
  font-size: 20px;
  color: #0f766e;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #64748b;
}

.modal-body {
  padding: 20px 25px 24px;
}

.jobgroup-form .form-row {
  display: flex;
  gap: 16px;
}

.jobgroup-form .form-group {
  flex: 1;
  margin-bottom: 12px;
}

.jobgroup-form label {
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 4px;
  display: block;
}

.jobgroup-form .form-control {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 8px 10px;
  font-size: 13px;
}

.jobgroup-form .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
}

.jobgroup-list {
  margin-top: 20px;
}

.jobgroup-list h4 {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
}

.jobgroup-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.jobgroup-table th,
.jobgroup-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}

.jobgroup-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #4b5563;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
}

.status-pill.active {
  background: #dcfce7;
  color: #15803d;
}

.status-pill.inactive {
  background: #fee2e2;
  color: #b91c1c;
}

.icon-btn.danger {
  color: #b91c1c;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 70px;
  }
  
  .position-management.nav-collapsed .dashboard-content {
    margin-left: 70px;
  }
  
  .bulk-actions-bar {
    min-width: 90%;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    padding: 15px;
  }
  
  .position-management.nav-collapsed .dashboard-content {
    margin-left: 0;
  }

  .modern-page-header {
    padding: 20px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .dashboard-title {
    font-size: 24px;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  .positions-grid {
    grid-template-columns: 1fr;
  }

  .bulk-actions-bar {
    position: static;
    transform: none;
    width: 100%;
    margin-top: 20px;
    border-radius: 16px;
  }

  .bulk-actions-content {
    flex-direction: column;
    gap: 15px;
  }

  .bulk-actions {
    flex-wrap: wrap;
    justify-content: center;
  }

  .pagination-section {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .footer-content {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
}

@media (max-width: 480px) {
  .dashboard-title {
    font-size: 20px;
  }

  .metric-content {
    padding-right: 60px;
  }

  .metric-icon {
    width: 50px;
    height: 50px;
  }

  .metric-icon i {
    font-size: 24px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>